/**
 * The pages of a rule estimate history, one member for each set of searches, read while the
 * preview shows them. One failed search fails the history. The key holds only the searches: the
 * threshold filters the pages on the client, so a drag of the slider keeps the member.
 */

import type { Atom } from 'jotai';

import { queryAtoms } from '../queryAtoms';
import { api } from '@/api';
import { getCanonicalQueries } from '@/lib/canonicalQueries';
import { getScopeKey } from '@/lib/scopeKey';
import type {
    EstimateHistoryRequest, EstimateHistorySnapshot, FetchedHistory,
} from '@/types/estimate';
import { PreviewHistory } from '@/types/estimate';

function getEstimateHistoryKey({ source, queries }: EstimateHistoryRequest): string {
    return getScopeKey({ source, queries: getCanonicalQueries(queries) });
}

async function loadHistory({ source, queries }: EstimateHistoryRequest): Promise<FetchedHistory> {
    switch (source) {
        case PreviewHistory.Scenes: {
            const pages = await Promise.all(queries.map((query) => api.searchEmbeddings(query)));
            return { source, pages };
        }
        case PreviewHistory.Objects:
        default: {
            const pages = await Promise.all(queries.map((query) => api.searchEvents(query)));
            return { source: PreviewHistory.Objects, pages };
        }
    }
}

const histories = queryAtoms<EstimateHistoryRequest, EstimateHistorySnapshot, FetchedHistory>({
    getRequestKey: getEstimateHistoryKey,
    getPending: (): EstimateHistorySnapshot => ({ history: null, isLoading: true, error: null }),
    load: loadHistory,
    merge: (_previousSnapshot, page): EstimateHistorySnapshot => ({
        history: page, isLoading: false, error: null,
    }),
});

/** The pages of the searches of `request`. */
export function estimateHistoryAtom(
    request: EstimateHistoryRequest,
): Atom<EstimateHistorySnapshot> {
    return histories.getMember(request);
}
